/**
 * StubCheckout — V2 Premium Experience (V2.0 Phase 5, Cluster A).
 *
 * Route: `/billing/stub-checkout`. In-app stub checkout confirmation page.
 * Stripe checkout in stub mode bounces here with
 * `?session=cs_stub_…&plan=member`. We display a decorative card form
 * (never POSTed anywhere) and a "Complete checkout" button that calls
 * /api/billing/stub-checkout/complete.
 */
import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, CreditCard, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/api/client";
import AuthShell from "./AuthShell";
import { V2Button } from "@/components/v2/FormPrimitives";

export default function StubCheckout() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session") || "";
  const planCode = params.get("plan") || "";
  const adminFallback = params.get("admin_fallback") === "1";
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const isAdminFallback = adminFallback && sessionId.startsWith("cs_admin_stub_");
  const valid = isAdminFallback || (sessionId.startsWith("cs_stub_") && planCode);

  async function complete() {
    if (!valid) return;
    setBusy(true);
    try {
      if (isAdminFallback) {
        await api.post("/payments/admin-stub-checkout/complete", { session_id: sessionId });
      } else {
        await api.post(
          `/billing/stub-checkout/complete?plan_code=${encodeURIComponent(planCode)}`,
          { session_id: sessionId },
        );
      }
      setDone(true);
      toast.success(
        isAdminFallback
          ? "Admin fallback subscription activated (Stripe unreachable)."
          : "Subscription activated (sandbox mode).",
      );
      const redirect = isAdminFallback
        ? "/account/billing?admin_fallback=1"
        : "/settings/billing?status=success";
      setTimeout(() => navigate(redirect, { replace: true }), 900);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  const titleMap = {
    invalid: "Invalid checkout session.",
    done: `You're on the ${planCode} plan.`,
    form: isAdminFallback ? "Admin stub checkout." : "Confirm checkout.",
  };
  const phase = !valid ? "invalid" : done ? "done" : "form";

  return (
    <AuthShell testId="stub-checkout-page" title={titleMap[phase]}>
      <div className="flex justify-end mb-4">
        <Link
          to="/pricing"
          data-testid="stub-checkout-back-to-pricing"
          className="inline-flex items-center gap-1.5 text-v2-body-sm text-v2-textMuted hover:text-v2-text transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to pricing
        </Link>
      </div>

      {phase === "invalid" && (
        <div
          data-testid="stub-checkout-invalid"
          className="rounded-v2-2xl bg-v2-surface border border-v2-border shadow-v2-card p-6 md:p-8 text-center"
        >
          <p className="text-v2-body-md text-v2-textMuted">
            This sandbox checkout link is malformed. Return to the pricing page and try again.
          </p>
        </div>
      )}

      {phase === "done" && (
        <div
          data-testid="stub-checkout-done"
          className="rounded-v2-2xl bg-v2-surface border border-v2-border shadow-v2-card p-6 md:p-8 text-center"
        >
          <CheckCircle2 className="w-9 h-9 mb-3 mx-auto text-v2-success" />
          <p className="text-v2-body-md text-v2-textMuted">Redirecting to billing…</p>
        </div>
      )}

      {phase === "form" && (
        <div className="rounded-v2-2xl bg-v2-surface border border-v2-border shadow-v2-card p-6 md:p-8">
          {isAdminFallback && (
            <div
              data-testid="stub-checkout-admin-banner"
              className="mb-6 rounded-v2-lg px-4 py-3 flex items-start gap-3 bg-v2-warningSoft border border-v2-warning/40 text-v2-warning"
            >
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <div className="text-v2-body-sm leading-relaxed">
                <div className="font-semibold">Admin stub checkout</div>
                <div className="opacity-90">
                  Stripe returned an authentication error — showing stub checkout for admin
                  testing. No real card will be charged. Non-admin users see the friendly
                  error toast instead.
                </div>
              </div>
            </div>
          )}
          <div className="text-v2-caption uppercase tracking-[0.16em] text-v2-accent mb-2">
            {isAdminFallback ? "Admin fallback" : "Sandbox mode"}
          </div>
          <p className="text-v2-body-md text-v2-textMuted mb-6">
            You're subscribing to the{" "}
            <strong className="text-v2-text">{planCode}</strong> plan.{" "}
            {isAdminFallback
              ? "In admin fallback mode, this creates a stub subscription so you can demo the paid-tier UI while Stripe auth is unavailable."
              : "In sandbox mode no real card is charged."}
          </p>

          <div className="rounded-v2-lg border border-v2-border bg-v2-surfaceSubtle p-4 mb-5">
            <div className="flex items-center gap-2 mb-3">
              <CreditCard className="w-4 h-4 text-v2-accent" />
              <span className="text-v2-caption uppercase tracking-[0.16em] text-v2-textMuted">
                Card details (display only)
              </span>
            </div>
            <input
              data-testid="stub-checkout-card"
              disabled
              value="4242 4242 4242 4242"
              className="w-full rounded-v2-md border border-v2-border bg-transparent px-3 py-2 text-v2-body-sm text-v2-textMuted font-mono"
            />
            <div className="grid grid-cols-2 gap-2 mt-2">
              <input
                disabled
                value="12 / 32"
                className="rounded-v2-md border border-v2-border bg-transparent px-3 py-2 text-v2-body-sm text-v2-textMuted font-mono"
              />
              <input
                disabled
                value="123"
                className="rounded-v2-md border border-v2-border bg-transparent px-3 py-2 text-v2-body-sm text-v2-textMuted font-mono"
              />
            </div>
          </div>

          <V2Button
            data-testid="stub-checkout-complete"
            disabled={busy}
            loading={busy}
            onClick={complete}
            fullWidth
          >
            {busy ? "Completing…" : "Complete checkout (sandbox)"}
          </V2Button>

          <p className="text-v2-body-sm text-v2-textSubtle mt-3 text-center">
            Session: <span className="font-mono text-v2-textMuted">{sessionId}</span>
          </p>
        </div>
      )}
    </AuthShell>
  );
}
